import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CustomizationRequest } from '@/types/marketplace';

export interface CartItem {
  /**
   * Clé de la ligne : l'article, puis la couleur, la taille et le flocage
   * s'il en a (« <article>:<couleur>:<taille>:<flocage> »). Le maillot rouge
   * en M et en L fait deux lignes, comme le rouge en M floqué DIOP et MANÉ.
   */
  id: string;
  /** Identifiant de l'article. Absent des paniers d'avant les couleurs : c'est alors `id`. */
  productId?: string;
  variantId?: string;
  color?: string;
  sizeId?: string;
  size?: string;
  /** Flocage demandé (nom, numéro). */
  customization?: CustomizationRequest;
  title: string;
  /** Prix unitaire, supplément de flocage compris. */
  price: number;
  quantity: number;
  image: string;
  seller: string;
  condition: string;
  stock: number;
}

interface CartStore {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'quantity'>) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
}

/** L'article d'une ligne de panier, y compris pour un panier d'avant les couleurs. */
export const cartProductId = (item: CartItem) => item.productId ?? item.id;

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      
      addItem: (item) => {
        const items = get().items;
        const existingItem = items.find((i) => i.id === item.id);
        
        if (existingItem) {
          set({
            items: items.map((i) =>
              i.id === item.id
                ? { ...i, quantity: Math.min(i.quantity + 1, item.stock) }
                : i
            ),
          });
        } else {
          set({ items: [...items, { ...item, quantity: 1 }] });
        }
      },
      
      removeItem: (id) => {
        set({ items: get().items.filter((item) => item.id !== id) });
      },
      
      updateQuantity: (id, quantity) => {
        if (quantity <= 0) {
          get().removeItem(id);
        } else {
          set({
            items: get().items.map((item) =>
              item.id === id ? { ...item, quantity } : item
            ),
          });
        }
      },
      
      clearCart: () => {
        set({ items: [] });
      },
      
      getTotalItems: () => {
        return get().items.reduce((total, item) => total + item.quantity, 0);
      },
      
      getTotalPrice: () => {
        return get().items.reduce((total, item) => total + item.price * item.quantity, 0);
      },
    }),
    {
      name: 'fconnect-cart-storage',
    }
  )
);
